import { Link } from "react-router-dom";
import { getCookieConsent } from "@/components/app/CookieBanner";
import { LockPreviewButton } from "@/components/app/LockPreviewButton";

const COLUMNS = [
  {
    heading: "Discover",
    links: [
      { label: "Browse universities", to: "/explore", testid: "footer-link-explore" },
      { label: "Browse cities", to: "/cities", testid: "footer-link-cities" },
      { label: "UK visa-friendly degrees", to: "/uk-visa-friendly-degrees", testid: "footer-link-uk-visa" },
      { label: "US STEM-OPT degrees", to: "/us-stem-opt-degrees", testid: "footer-link-us-stem" },
      { label: "US liberal arts colleges", to: "/us-liberal-arts-colleges", testid: "footer-link-us-lac" },
      { label: "UK conservatoires", to: "/uk-conservatoires", testid: "footer-link-uk-conservatoires" },
    ],
  },
  {
    heading: "Tools",
    links: [
      { label: "Admission chance calculator", to: "/admission", testid: "footer-link-admission" },
      { label: "Grade calculator", to: "/grade-calculator", testid: "footer-link-grade-calc" },
      { label: "Advanced search", to: "/advanced", testid: "footer-link-advanced" },
      { label: "Side-by-side comparison", to: "/compare", testid: "footer-link-compare" },
      { label: "Cross-country cost", to: "/explore/cross-country", testid: "footer-link-cross-country" },
      { label: "City matcher", to: "/cities/matcher", testid: "footer-link-city-matcher" },
    ],
  },
  {
    heading: "Career signals",
    links: [
      { label: "Highest-earning courses", to: "/highest-earning", testid: "footer-link-highest-earning" },
      { label: "Industry hubs", to: "/cities/hubs", testid: "footer-link-industry-hubs" },
      { label: "Firm guides", to: "/firm-guides", testid: "footer-link-firm-guides" },
      { label: "Top employers", to: "/employers", testid: "footer-link-employers" },
      { label: "Compare firm salaries", to: "/employers/compare", testid: "footer-link-employer-compare" },
      { label: "Trending verdicts", to: "/compare/trending", testid: "footer-link-trending" },
    ],
  },
  {
    heading: "Your account",
    links: [
      { label: "Saved searches", to: "/saved", testid: "footer-link-saved" },
      { label: "Your shortlist", to: "/shortlist", testid: "footer-link-shortlist" },
      { label: "For universities", to: "/uni-portal", testid: "footer-link-uni-portal" },
      { label: "WUA Plus", to: "/plus", testid: "footer-link-plus" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "How it works", to: "/about", testid: "footer-link-how-it-works" },
      { label: "About us", to: "/legal/about", testid: "footer-link-about" },
      { label: "Methodology", to: "/methodology", testid: "footer-link-methodology" },
      { label: "Corrections log", to: "/methodology/corrections", testid: "footer-link-corrections" },
      { label: "Contact", to: "/legal/contact", testid: "footer-link-contact" },
      { label: "Privacy Policy", to: "/legal/privacy", testid: "footer-link-privacy" },
      { label: "Terms of Service", to: "/legal/terms", testid: "footer-link-terms" },
      { label: "Cookie Policy", to: "/legal/cookies", testid: "footer-link-cookies" },
      { label: "Accessibility", to: "/legal/accessibility", testid: "footer-link-accessibility" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="mt-24 border-t border-stone-200 bg-[#F3F1EC]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1fr_1fr] md:grid-cols-2">
          <div>
            <Link to="/" data-testid="footer-link-home" className="inline-flex flex-col items-start gap-1">
              <img
                src="/logo.png"
                alt="WhichUniversityAdvisor"
                width="240"
                height="96"
                style={{ height: "auto", width: "auto", maxWidth: "240px", maxHeight: "96px" }}
                className="object-contain"
              />
              <span className="text-[10px] font-underground-sc tracking-[0.14em] text-stone-500">
                WhichUniversityAdvisor.com
              </span>
            </Link>
            <p className="mt-4 text-sm text-stone-600 leading-relaxed max-w-xs">
              Not "which universities are best?", but which universities are best
              for <em>you</em>, for <em>your</em> budget, for the right course and
              the future you want.
            </p>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <div className="label-uppercase mb-3">{col.heading}</div>
              <ul className="text-sm space-y-2 text-stone-700">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      data-testid={link.testid}
                      className="hover:text-[#4C1D95] hover:underline underline-offset-4 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-500 font-mono">
          <LockPreviewButton />
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} WhichUniversityAdvisor.com · All rights reserved
            <span className="mx-2 text-stone-400" aria-hidden>·</span>
            <span data-testid="footer-parent-attribution">
              A{" "}
              <a
                href="https://which-media.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-stone-800"
              >
                Which Media FZ LLC
              </a>
              {" "}service
            </span>
            <button
              type="button"
              data-testid="footer-manage-cookies"
              onClick={() => {
                try {
                  localStorage.removeItem("wua:cookie-consent");
                } catch {}
                // Fire the same event the banner listens to so it re-opens
                // without a page reload.
                try {
                  window.dispatchEvent(
                    new CustomEvent("wua:cookie-consent-changed", { detail: "show" })
                  );
                } catch {}
              }}
              className="ml-3 underline underline-offset-2 hover:text-stone-800"
            >
              Manage cookies
              {getCookieConsent() ? ` · ${getCookieConsent()}` : ""}
            </button>
          </div>
          <div className="text-stone-600 max-w-xl text-center sm:text-right leading-relaxed">
            Editorial promise: we publish warnings alongside strengths. If a university has weak
            student satisfaction, poor housing or a difficult visa route, we say so.
          </div>
        </div>
      </div>
    </footer>
  );
}
